/**
 * Pixel-grid → SVG renderers for the Pixel SVG Canvas tool.
 *
 * Pixel buffer: a `Uint32Array` of length `width * height`, packed as
 * `(255 << 24) | (r << 16) | (g << 8) | b`. The value `0` means "empty".
 */

export const EMPTY_CELL = 0;

export type OutputMode = "raw" | "greedy-rects" | "paths";

/** How each grid cell is drawn in SVG output (editor grid stays square pixels). */
export const CELL_SHAPE_OPTIONS = ["square", "rounded", "circle"] as const;
export type CellShape = (typeof CELL_SHAPE_OPTIONS)[number];

export function packColor(r: number, g: number, b: number): number {
    return (((255 & 0xff) << 24) | ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff)) >>> 0;
}

export function unpackColor(v: number): { r: number; g: number; b: number; a: number } {
    return {
        a: (v >>> 24) & 0xff,
        r: (v >>> 16) & 0xff,
        g: (v >>> 8) & 0xff,
        b: v & 0xff,
    };
}

export function colorToHex(v: number): string {
    const r = (v >>> 16) & 0xff;
    const g = (v >>> 8) & 0xff;
    const b = v & 0xff;
    const hex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${hex(r)}${hex(g)}${hex(b)}`;
}

export function parseHexColor(hex: string): number {
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return packColor(0, 0, 0);
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
        return packColor(0, 0, 0);
    }
    return packColor(r, g, b);
}

export interface MergedRect {
    x: number;
    y: number;
    w: number;
    h: number;
    color: number;
}

/**
 * Greedy meshing: groups adjacent same-color cells into the largest possible
 * axis-aligned rectangles. Returns one rect per merged region.
 */
export function greedyMesh(pixels: Uint32Array, width: number, height: number): MergedRect[] {
    const visited = new Uint8Array(width * height);
    const rects: MergedRect[] = [];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            if (visited[idx]) continue;
            const color = pixels[idx];
            if (color === EMPTY_CELL) {
                visited[idx] = 1;
                continue;
            }

            let runW = 1;
            while (x + runW < width) {
                const ni = y * width + (x + runW);
                if (visited[ni] || pixels[ni] !== color) break;
                runW++;
            }

            let runH = 1;
            extendDown: while (y + runH < height) {
                for (let dx = 0; dx < runW; dx++) {
                    const ni = (y + runH) * width + (x + dx);
                    if (visited[ni] || pixels[ni] !== color) break extendDown;
                }
                runH++;
            }

            for (let dy = 0; dy < runH; dy++) {
                for (let dx = 0; dx < runW; dx++) {
                    visited[(y + dy) * width + (x + dx)] = 1;
                }
            }

            rects.push({ x, y, w: runW, h: runH, color });
        }
    }
    return rects;
}

export interface RenderResult {
    /** SVG inner body (no surrounding `<svg>`). */
    body: string;
    /** Number of top-level shape elements emitted (rects or paths). */
    count: number;
}

function fmt(n: number): string {
    const rounded = Math.round(n * 10_000) / 10_000;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** Corner radii for “rounded” cells (clamped so arcs are valid). */
export function cellCornerRadii(cellW: number, cellH: number): { rx: number; ry: number } {
    const base = Math.min(cellW, cellH) * 0.35;
    return {
        rx: Math.min(base, cellW / 2),
        ry: Math.min(base, cellH / 2),
    };
}

function emitCellElement(
    x: number,
    y: number,
    cellW: number,
    cellH: number,
    color: number,
    shape: CellShape,
): string {
    const fill = colorToHex(color);
    const px = fmt(x * cellW);
    const py = fmt(y * cellH);
    const fw = fmt(cellW);
    const fh = fmt(cellH);
    if (shape === "square") {
        return `<rect x="${px}" y="${py}" width="${fw}" height="${fh}" fill="${fill}"/>`;
    }
    if (shape === "rounded") {
        const { rx, ry } = cellCornerRadii(cellW, cellH);
        return `<rect x="${px}" y="${py}" width="${fw}" height="${fh}" rx="${fmt(rx)}" ry="${fmt(ry)}" fill="${fill}"/>`;
    }
    const cx = fmt(x * cellW + cellW / 2);
    const cy = fmt(y * cellH + cellH / 2);
    const r = fmt(Math.min(cellW, cellH) / 2);
    return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"/>`;
}

function roundedRectPathD(
    x: number,
    y: number,
    w: number,
    h: number,
    rxIn: number,
    ryIn: number,
): string {
    const rx = Math.min(rxIn, w / 2);
    const ry = Math.min(ryIn, h / 2);
    if (rx <= 0 || ry <= 0) {
        return `M${fmt(x)},${fmt(y)}h${fmt(w)}v${fmt(h)}h${fmt(-w)}Z`;
    }
    const x0 = x + rx;
    const x1 = x + w - rx;
    const y0 = y + ry;
    const y1 = y + h - ry;
    return (
        `M${fmt(x0)},${fmt(y)}` +
        `L${fmt(x1)},${fmt(y)}` +
        `A${fmt(rx)},${fmt(ry)} 0 0 1 ${fmt(x + w)},${fmt(y0)}` +
        `L${fmt(x + w)},${fmt(y1)}` +
        `A${fmt(rx)},${fmt(ry)} 0 0 1 ${fmt(x1)},${fmt(y + h)}` +
        `L${fmt(x0)},${fmt(y + h)}` +
        `A${fmt(rx)},${fmt(ry)} 0 0 1 ${fmt(x)},${fmt(y1)}` +
        `L${fmt(x)},${fmt(y0)}` +
        `A${fmt(rx)},${fmt(ry)} 0 0 1 ${fmt(x0)},${fmt(y)}Z`
    );
}

function circlePathD(cx: number, cy: number, r: number): string {
    const rs = fmt(r);
    return (
        `M${fmt(cx - r)},${fmt(cy)}` +
        `A${rs},${rs} 0 1 0 ${fmt(cx + r)},${fmt(cy)}` +
        `A${rs},${rs} 0 1 0 ${fmt(cx - r)},${fmt(cy)}Z`
    );
}

/** One `<rect>` / rounded `<rect>` / `<circle>` per filled cell. */
export function renderRectsRaw(
    pixels: Uint32Array,
    width: number,
    height: number,
    outputWidth: number = width,
    outputHeight: number = height,
    cellShape: CellShape = "square",
): RenderResult {
    const cellW = outputWidth / width;
    const cellH = outputHeight / height;
    const parts: string[] = [];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const v = pixels[y * width + x];
            if (v === EMPTY_CELL) continue;
            parts.push(emitCellElement(x, y, cellW, cellH, v, cellShape));
        }
    }
    return { body: parts.join(""), count: parts.length };
}

/** Greedy-merged same-color rectangles. Multiple `<rect>` per color, but far fewer than raw. */
export function renderRectsGreedy(
    pixels: Uint32Array,
    width: number,
    height: number,
    outputWidth: number = width,
    outputHeight: number = height,
    cellShape: CellShape = "square",
): RenderResult {
    if (cellShape !== "square") {
        return renderRectsRaw(
            pixels,
            width,
            height,
            outputWidth,
            outputHeight,
            cellShape,
        );
    }
    const cellW = outputWidth / width;
    const cellH = outputHeight / height;
    const rects = greedyMesh(pixels, width, height);
    const parts = rects.map(
        (r) =>
            `<rect x="${fmt(r.x * cellW)}" y="${fmt(r.y * cellH)}" width="${fmt(r.w * cellW)}" height="${fmt(r.h * cellH)}" fill="${colorToHex(r.color)}"/>`,
    );
    return { body: parts.join(""), count: rects.length };
}

/** One `<path>` per unique color from per-cell circles or rounded rects (no greedy merge). */
function renderPathsByColorShapedCells(
    pixels: Uint32Array,
    width: number,
    height: number,
    outputWidth: number,
    outputHeight: number,
    shape: "rounded" | "circle",
): RenderResult {
    const cellW = outputWidth / width;
    const cellH = outputHeight / height;
    const { rx, ry } = cellCornerRadii(cellW, cellH);
    const byColor = new Map<number, { x: number; y: number }[]>();
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const v = pixels[y * width + x];
            if (v === EMPTY_CELL) continue;
            let list = byColor.get(v);
            if (!list) {
                list = [];
                byColor.set(v, list);
            }
            list.push({ x, y });
        }
    }
    const parts: string[] = [];
    for (const [color, cells] of byColor) {
        const d = cells
            .map(({ x, y }) => {
                const ox = x * cellW;
                const oy = y * cellH;
                if (shape === "circle") {
                    return circlePathD(
                        ox + cellW / 2,
                        oy + cellH / 2,
                        Math.min(cellW, cellH) / 2,
                    );
                }
                return roundedRectPathD(ox, oy, cellW, cellH, rx, ry);
            })
            .join("");
        parts.push(`<path d="${d}" fill="${colorToHex(color)}"/>`);
    }
    return { body: parts.join(""), count: parts.length };
}

/** One `<path>` per unique color. Each path is the union of greedy-merged sub-rectangles. */
export function renderPathsByColor(
    pixels: Uint32Array,
    width: number,
    height: number,
    outputWidth: number = width,
    outputHeight: number = height,
    cellShape: CellShape = "square",
): RenderResult {
    if (cellShape === "circle" || cellShape === "rounded") {
        return renderPathsByColorShapedCells(
            pixels,
            width,
            height,
            outputWidth,
            outputHeight,
            cellShape,
        );
    }
    const cellW = outputWidth / width;
    const cellH = outputHeight / height;
    const rects = greedyMesh(pixels, width, height);
    const byColor = new Map<number, MergedRect[]>();
    for (const r of rects) {
        const arr = byColor.get(r.color);
        if (arr) arr.push(r);
        else byColor.set(r.color, [r]);
    }
    const parts: string[] = [];
    for (const [color, list] of byColor) {
        const d = list
            .map(
                (r) =>
                    `M${fmt(r.x * cellW)} ${fmt(r.y * cellH)}h${fmt(r.w * cellW)}v${fmt(r.h * cellH)}h-${fmt(r.w * cellW)}Z`,
            )
            .join("");
        parts.push(`<path d="${d}" fill="${colorToHex(color)}"/>`);
    }
    return { body: parts.join(""), count: parts.length };
}

export function renderPixels(
    pixels: Uint32Array,
    width: number,
    height: number,
    mode: OutputMode,
    outputWidth: number = width,
    outputHeight: number = height,
    cellShape: CellShape = "square",
): RenderResult {
    switch (mode) {
        case "raw":
            return renderRectsRaw(
                pixels,
                width,
                height,
                outputWidth,
                outputHeight,
                cellShape,
            );
        case "greedy-rects":
            return renderRectsGreedy(
                pixels,
                width,
                height,
                outputWidth,
                outputHeight,
                cellShape,
            );
        case "paths":
            return renderPathsByColor(
                pixels,
                width,
                height,
                outputWidth,
                outputHeight,
                cellShape,
            );
    }
}

/** Wrap a renderer body in a complete SVG document string ready for download. */
export function buildSvgDocument(
    body: string,
    outputWidth: number,
    outputHeight: number,
    cellShape: CellShape = "square",
): string {
    const shapeRendering =
        cellShape === "square" ? "crispEdges" : "geometricPrecision";
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${outputWidth}" height="${outputHeight}" viewBox="0 0 ${outputWidth} ${outputHeight}" shape-rendering="${shapeRendering}">${body}</svg>`;
}

export function countFilledCells(pixels: Uint32Array): number {
    let n = 0;
    for (let i = 0; i < pixels.length; i++) {
        if (pixels[i] !== EMPTY_CELL) n++;
    }
    return n;
}

/**
 * Iterative 4-way flood fill on a pixel buffer. Mutates `pixels` in place.
 * Returns the number of cells filled.
 */
export function floodFill(
    pixels: Uint32Array,
    width: number,
    height: number,
    startX: number,
    startY: number,
    newColor: number,
): number {
    if (startX < 0 || startX >= width || startY < 0 || startY >= height) return 0;
    const startIdx = startY * width + startX;
    const target = pixels[startIdx];
    if (target === newColor) return 0;

    const stack: number[] = [startIdx];
    let filled = 0;
    while (stack.length) {
        const idx = stack.pop()!;
        if (pixels[idx] !== target) continue;
        pixels[idx] = newColor;
        filled++;
        const x = idx % width;
        const y = (idx - x) / width;
        if (x > 0) stack.push(idx - 1);
        if (x < width - 1) stack.push(idx + 1);
        if (y > 0) stack.push(idx - width);
        if (y < height - 1) stack.push(idx + width);
    }
    return filled;
}

/** Bresenham's line algorithm: enumerates integer cells from (x0,y0) to (x1,y1) inclusive. */
export function bresenhamLine(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    visit: (x: number, y: number) => void,
): void {
    let cx = x0;
    let cy = y0;
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    while (true) {
        visit(cx, cy);
        if (cx === x1 && cy === y1) return;
        const e2 = 2 * err;
        if (e2 >= dy) {
            err += dy;
            cx += sx;
        }
        if (e2 <= dx) {
            err += dx;
            cy += sy;
        }
    }
}

/** Stamp a square brush of side `size` centered on (cx, cy) into `pixels`. */
export function stampBrush(
    pixels: Uint32Array,
    width: number,
    height: number,
    cx: number,
    cy: number,
    size: number,
    color: number,
): void {
    const half = Math.floor(size / 2);
    const x0 = Math.max(0, cx - half);
    const y0 = Math.max(0, cy - half);
    const x1 = Math.min(width - 1, cx + (size - 1 - half));
    const y1 = Math.min(height - 1, cy + (size - 1 - half));
    for (let y = y0; y <= y1; y++) {
        const row = y * width;
        for (let x = x0; x <= x1; x++) {
            pixels[row + x] = color;
        }
    }
}

/** Filled axis-aligned rectangle from corner (x0,y0) to (x1,y1) inclusive. */
export function fillRect(
    pixels: Uint32Array,
    width: number,
    height: number,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    color: number,
): void {
    const ax = Math.max(0, Math.min(x0, x1));
    const ay = Math.max(0, Math.min(y0, y1));
    const bx = Math.min(width - 1, Math.max(x0, x1));
    const by = Math.min(height - 1, Math.max(y0, y1));
    for (let y = ay; y <= by; y++) {
        const row = y * width;
        for (let x = ax; x <= bx; x++) {
            pixels[row + x] = color;
        }
    }
}

/** Filled ellipse inscribed in the bounding box (x0,y0)–(x1,y1). Midpoint algorithm. */
export function fillEllipse(
    pixels: Uint32Array,
    width: number,
    height: number,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    color: number,
): void {
    const ax = Math.min(x0, x1);
    const ay = Math.min(y0, y1);
    const bx = Math.max(x0, x1);
    const by = Math.max(y0, y1);
    const cx = (ax + bx) / 2;
    const cy = (ay + by) / 2;
    const rx = (bx - ax) / 2;
    const ry = (by - ay) / 2;
    if (rx <= 0 || ry <= 0) {
        fillRect(pixels, width, height, ax, ay, bx, by, color);
        return;
    }
    const rx2 = rx * rx;
    const ry2 = ry * ry;
    const yStart = Math.max(0, Math.floor(ay));
    const yEnd = Math.min(height - 1, Math.ceil(by));
    for (let y = yStart; y <= yEnd; y++) {
        const dy = y + 0.5 - cy;
        const norm = 1 - (dy * dy) / ry2;
        if (norm < 0) continue;
        const dx = rx * Math.sqrt(norm);
        const xStart = Math.max(0, Math.floor(cx - dx));
        const xEnd = Math.min(width - 1, Math.ceil(cx + dx) - 1);
        const row = y * width;
        for (let x = xStart; x <= xEnd; x++) {
            const ddx = x + 0.5 - cx;
            if ((ddx * ddx) / rx2 + (dy * dy) / ry2 <= 1) {
                pixels[row + x] = color;
            }
        }
    }
}

/** Draws a thick line by stamping a square brush along Bresenham's line. */
export function drawThickLine(
    pixels: Uint32Array,
    width: number,
    height: number,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    thickness: number,
    color: number,
): void {
    bresenhamLine(x0, y0, x1, y1, (x, y) => {
        stampBrush(pixels, width, height, x, y, thickness, color);
    });
}
